/* eslint-disable @typescript-eslint/no-unused-vars */
import styled from "styled-components";

import useFeatureFlags from "hooks/useFeatureFlags";

import { EntityKind } from "../../models/entityKind";
import DateRangeSelect from "./DateRangeSelect";
import ForecastSourceSelect from "./ForecastSourceSelect";
import ForecastToggle from "./ForecastToggle";
import { useGroupByStore } from "./GroupByStoreProvider";
import PdenSourceSelect from "./PdenSourceSelect";
import ProductSelect from "./ProductSelect";
import { selectEntityKind } from "./groupBySelectors";
import { useProductSelect, useSelectedCategory } from "./hooks";
import { useForecastToggle } from "./hooks/useForecastToggle";

interface ProductToolbarProps {
  toolbarActive?: boolean;
  toolbarFocusedIndex?: Maybe<number>;
  onSelectRef?: (index: number, selectEl: Maybe<HTMLElement>) => void;
  hideDateRange?: boolean;
  hideSourceControls?: boolean;
  hideForecastTrumping?: boolean;
}

function ProductToolbar({
  toolbarActive = false,
  toolbarFocusedIndex = null,
  onSelectRef,
  hideDateRange = false,
  hideSourceControls = false,
  hideForecastTrumping = false
}: ProductToolbarProps = {}) {
  const fields = useSelectedCategory();
  const entityKind = useGroupByStore(selectEntityKind);
  const forecastToggle = useForecastToggle();
  const productSelect = useProductSelect();
  const { hasFeature } = useFeatureFlags();
  const hideCO2Options = !hasFeature("Midstream Allocation Model");

  // do not render if selected category has no product fields
  if (!fields.hasProductFields) return null;

  const showsWellSourceControls = entityKind === EntityKind.Well && !hideSourceControls;
  let componentIndex = 0;

  if (fields.hasProductFields && fields.hasPerformaceFields)
    return (
      <WrapperRow>
        <Wrapper>
          <ProductSelect
            selectedProduct={productSelect.selectedProduct}
            productOptions={productSelect.productOptions}
            onProductChange={productSelect.onProductChange}
            hideCO2Options={hideCO2Options}
            isFocused={toolbarActive && toolbarFocusedIndex === componentIndex}
            onSelectRef={onSelectRef}
            componentIndex={componentIndex}
          />
          {showsWellSourceControls &&
            (() => {
              componentIndex++;
              return (
                <ForecastToggle
                  isForecastToggleOn={forecastToggle.isForecastToggleOn}
                  onToggle={forecastToggle.onToggle}
                  isFocused={toolbarActive && toolbarFocusedIndex === componentIndex}
                  onSelectRef={onSelectRef}
                  componentIndex={componentIndex}
                />
              );
            })()}
        </Wrapper>
        <WrapperRow>
          {showsWellSourceControls &&
            (() => {
              componentIndex++;
              return (
                <PdenSourceSelect
                  isFocused={toolbarActive && toolbarFocusedIndex === componentIndex}
                  onSelectRef={onSelectRef}
                  componentIndex={componentIndex}
                />
              );
            })()}
          {forecastToggle.isForecastToggleOn &&
            !hideSourceControls &&
            (() => {
              componentIndex++;
              const rescatIndex = componentIndex + 1;
              const result = (
                <ForecastSourceSelect
                  showLabel={true}
                  hideForecastTrumping={hideForecastTrumping}
                  isFocused={toolbarActive && toolbarFocusedIndex === componentIndex}
                  rescatFocused={toolbarActive && toolbarFocusedIndex === rescatIndex}
                  onSelectRef={onSelectRef}
                  componentIndex={componentIndex}
                  rescatComponentIndex={rescatIndex}
                />
              );
              componentIndex++; // Account for the rescat select
              return result;
            })()}
          {!hideDateRange &&
            (() => {
              componentIndex++;
              return (
                <DateRangeSelect
                  showLabel={true}
                  isFocused={toolbarActive && toolbarFocusedIndex === componentIndex}
                  onSelectRef={onSelectRef}
                  componentIndex={componentIndex}
                />
              );
            })()}
        </WrapperRow>
      </WrapperRow>
    );

  if (fields.hasProductFields && fields.hasForecastFields) {
    componentIndex = 0; // Reset index for this branch
    return (
      <WrapperRow>
        <Wrapper>
          <ProductSelect
            selectedProduct={productSelect.selectedProduct}
            productOptions={productSelect.productOptions}
            onProductChange={productSelect.onProductChange}
            hideCO2Options={hideCO2Options}
            isFocused={toolbarActive && toolbarFocusedIndex === componentIndex}
            onSelectRef={onSelectRef}
            componentIndex={componentIndex}
          />
        </Wrapper>
        <WrapperRow>
          {!hideSourceControls &&
            (() => {
              componentIndex++;
              const rescatIndex = componentIndex + 1;
              const result = (
                <ForecastSourceSelect
                  showLabel={true}
                  hideForecastTrumping={hideForecastTrumping}
                  isFocused={toolbarActive && toolbarFocusedIndex === componentIndex}
                  rescatFocused={toolbarActive && toolbarFocusedIndex === rescatIndex}
                  onSelectRef={onSelectRef}
                  componentIndex={componentIndex}
                  rescatComponentIndex={rescatIndex}
                />
              );
              componentIndex++; // Account for the rescat select
              return result;
            })()}
        </WrapperRow>
      </WrapperRow>
    );
  }
}

export default ProductToolbar;

const Wrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr max-content;
  align-content: center;
  gap: var(--space-2);
  padding: 0 var(--space-2);
`;

const WrapperRow = styled.div`
  display: flex;
  padding: 0 var(--space-2);
  gap: 10px;
  flex-direction: column;
`;
